import React, { useEffect, useState } from "react";
import { Center, Loader, Text } from "@mantine/core";
import {
  getLocations,
  createLocation,
  updateLocation,
  toggleLocationActive,
} from "../../../api/LocationAPI"


/* ================= TYPES ================= */

type Location = {
  id: string;
  code: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  isActive: boolean;
};

type LocationForm = {
  code: string;
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
};

const validateLocationForm = (form: LocationForm) => {
  const errors: Partial<Record<keyof LocationForm, string>> = {};
  const hasLetters = (value: string) => /[A-Za-z]/.test(value);
  const validText = (value: string) => /^[A-Za-z0-9 .,'()&/-]+$/.test(value) && hasLetters(value);

  if (!/^[A-Za-z0-9_-]{2,30}$/.test(form.code.trim())) errors.code = 'Code must be 2–30 letters, numbers, hyphens, or underscores.';
  if (form.name.trim().length < 2 || form.name.trim().length > 100 || !validText(form.name.trim())) {
    errors.name = 'Enter a 2–100 character name containing letters; numbers and common punctuation are allowed.';
  }
  if (form.address.trim().length < 5 || form.address.trim().length > 255 || !validText(form.address.trim())) {
    errors.address = 'Enter an address of 5–255 characters containing letters.';
  }
  (['city', 'state', 'country'] as const).forEach((field) => {
    const value = form[field].trim();
    if (value.length < 2 || value.length > 100 || !validText(value)) {
      errors[field] = `${field[0].toUpperCase()}${field.slice(1)} must be 2–100 characters and contain letters.`;
    }
  });

  const country = form.country.trim().toLowerCase();
  const pincode = form.pincode.trim();
  const validPincode = country === 'india'
    ? /^\d{6}$/.test(pincode)
    : country === 'united states' || country === 'usa' || country === 'us'
      ? /^\d{5}(?:-\d{4})?$/.test(pincode)
      : country === 'canada'
        ? /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(pincode)
        : /^[A-Za-z0-9][A-Za-z0-9 -]{1,10}[A-Za-z0-9]$/.test(pincode);
  if (!validPincode) errors.pincode = 'Enter a valid postal code for the selected country.';

  return errors;
};


/* ================= API (TEMP MOCK / REPLACE WITH REAL API) ================= */
// 👉 Replace these with your real API imports
// import {
//   getLocations,
//   createLocation,
//   updateLocation,
//   toggleLocationActive,
// } from "../../../api/locationAPI";

/* ================= COMPONENT ================= */

const LocationManagement: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchCity, setSearchCity] = useState('');// Search Only city Name

  const [form, setForm] = useState({
  code: '',
  name: '',
  address: '',
  city: '',
  state: '',
  country: '',
  pincode: '',
})
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof LocationForm, string>>>({});

  const updateFormField = (field: keyof LocationForm, value: string) => {
    const next = { ...form, [field]: value };
    setForm(next);
    if (Object.keys(formErrors).length) setFormErrors(validateLocationForm(next));
  };

  /* Search City Name */
  const filteredLocations = locations.filter((loc) =>
    loc.city?.toLowerCase().includes(searchCity.toLowerCase())
  );


  /* ================= EFFECT ================= */

  useEffect(() => {
    fetchLocations();
  }, []);

  /* ================= API HANDLERS ================= */

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getLocations();
      if (res?.statusCode === 200) {
        setLocations(res.data || []);
      } else {
        setError("Failed to load locations");
      }
    } catch {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (loc: any) => {
  setEditingId(loc.id);
  setForm({
    code: loc.code,
    name: loc.name,
    address: loc.address || '',
    city: loc.city || '',
    state: loc.state || '',
    country: loc.country || '',
    pincode: loc.pincode || '',
  });
  setFormErrors({});
  setShowModal(true);
};


  const handleSave = async () => {
    const errors = validateLocationForm(form);
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }

    setSaving(true);
    try {
      let res;
      if (editingId) {
        res = await updateLocation(editingId, form);
      } else {
        // res = await createLocation({
        //   code: form.code,
        //   name: form.name,
        // });
        res = await createLocation(form);
      }

      if (res?.statusCode === 200 || res?.statusCode === 201) {
        setShowModal(false);
        setEditingId(null);
        setForm({
          code: '',
          name: '',
          address: '',
          city: '',
          state: '',
          country: '',
          pincode: '',
        });
        setFormErrors({});
        fetchLocations();
      } else {
        alert("Failed to save location");
      }
    } catch {
      alert("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (id: string) => {
    const res = await toggleLocationActive(id);
    if (res?.statusCode === 200) {
      fetchLocations();
    }
  };

  /* ================= UI ================= */

  return (
    <div className="card mt-4">
      <div className="card-body">

        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0">Location Master</h3>
           {/* Search Button */}
          <div className="row mb-2">
            <div className="col-md-12 position-relative">
              <input
                type="text"
                className="form-control pe-5"
                placeholder="Search by city..."
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
              />

              {searchCity && (
                <span
                  onClick={() => setSearchCity('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#888',
                  }}
                  title="Clear"
                >
                  ❌
                </span>
              )}
            </div>
          </div>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => {
              setFormErrors({});
              setShowModal(true);
            }}
          >
            + Add Location
          </button>
        </div>

        {/* LOADING */}
        {loading && (
          <Center>
            <Loader />
          </Center>
        )}

        {/* ERROR */}
        {error && !loading && (
          <Center>
            <Text color="red">{error}</Text>
          </Center>
        )}

        {/* TABLE */}
        {!loading && !error && (
          
          <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr className="fw-bold text-uppercase text-muted">
                <th>Code</th>
                <th>Name</th>
                <th>Address</th>
                <th>City</th>
                <th>Status</th>
                <th style={{ width: 220, minWidth: 190 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredLocations.length > 0 ? (
                filteredLocations.map((l) => (
                  <tr key={l.id}>
                    <td>{l.code}</td>
                    <td>{l.name}</td>
                    <td>{l.address || '-'}</td>
                     <td>{l.city || '-'}</td>
                    <td>
                      <span
                        className={`badge ${
                          l.isActive ? "bg-success" : "bg-secondary"
                        }`}
                      >
                        {l.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ minWidth: 190 }}>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEdit(l)}
                      >
                        Edit
                      </button>

                      <button
                        className={`btn btn-sm ${
                          l.isActive
                            ? "btn-outline-danger"
                            : "btn-outline-success"
                        }`}
                        onClick={() => handleToggleStatus(l.id)}
                      >
                        {l.isActive ? "Deactivate" : "Activate"}
                      </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No locations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        )}

        {/* MODAL */}
        {showModal && (
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-md">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingId ? "Edit Location" : "Add Location"}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowModal(false);
                      setEditingId(null);
                      setForm({
                        code: '',
                        name: '',
                        address: '',
                        city: '',
                        state: '',
                        country: '',
                        pincode: '',
                      });
                    }}
                  />
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label required">Location Code</label>
                    <input
                      className={`form-control ${formErrors.code ? 'is-invalid' : ''}`}
                      value={form.code}
                      onChange={(e) =>
                        updateFormField('code', e.target.value)
                      }
                    />
                    {formErrors.code && <div className="invalid-feedback">{formErrors.code}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label required">Location Name</label>
                    <input
                      className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                      value={form.name}
                      onChange={(e) =>
                        updateFormField('name', e.target.value)
                      }
                    />
                    {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label required">Address</label>
                    <input
                      className={`form-control ${formErrors.address ? 'is-invalid' : ''}`}
                      value={form.address}
                      onChange={(e) => updateFormField('address', e.target.value)}
                    />
                    {formErrors.address && <div className="invalid-feedback">{formErrors.address}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label required">City</label>
                    <input
                      className={`form-control ${formErrors.city ? 'is-invalid' : ''}`}
                      value={form.city}
                      onChange={(e) => updateFormField('city', e.target.value)}
                    />
                    {formErrors.city && <div className="invalid-feedback">{formErrors.city}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label required">State</label>
                    <input
                      className={`form-control ${formErrors.state ? 'is-invalid' : ''}`}
                      value={form.state}
                      onChange={(e) => updateFormField('state', e.target.value)}
                    />
                    {formErrors.state && <div className="invalid-feedback">{formErrors.state}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label required">Country</label>
                    <input
                      className={`form-control ${formErrors.country ? 'is-invalid' : ''}`}
                      value={form.country}
                      onChange={(e) => updateFormField('country', e.target.value)}
                    />
                    {formErrors.country && <div className="invalid-feedback">{formErrors.country}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label required">Pincode</label>
                    <input
                      className={`form-control ${formErrors.pincode ? 'is-invalid' : ''}`}
                      value={form.pincode}
                      onChange={(e) => updateFormField('pincode', e.target.value)}
                    />
                    {formErrors.pincode && <div className="invalid-feedback">{formErrors.pincode}</div>}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary"
                    disabled={saving}
                    onClick={handleSave}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ================= EXPORT (IMPORTANT) ================= */

export default LocationManagement;
